import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { CATEGORY_LABELS } from '../navigation';
import { colors, layout, radius } from '../design-system/tokens';

/**
 * master QA 매장 선택기.
 *
 * 매장이 늘어나도 쓸 수 있도록 Select 대신 검색 가능한 Autocomplete 를 쓴다.
 * - 매장명 / code 둘 다로 검색된다 (운영자는 code 로 기억하는 경우가 많다)
 * - 카테고리별 그룹 헤더
 * - 최근 본 매장을 맨 위로 올린다
 */
/**
 * 매장 검색 술어 — 매장명과 code 둘 다 매칭한다.
 * (운영자는 'jeonju' 처럼 code 로 기억하는 경우가 많다)
 */
export function matchesStoreKeyword(option, keyword) {
  const text = String(keyword ?? '').trim().toLowerCase();
  if (!text) return true;
  return (
    String(option?.name ?? '').toLowerCase().includes(text) ||
    String(option?.code ?? '').toLowerCase().includes(text)
  );
}

const StorePicker = ({
  options = [],
  value,
  recentStoreIds = [],
  onChange,
  disabled,
  compact = false,
}) => {
  const sorted = useMemo(() => {
    const recentRank = new Map(recentStoreIds.map((id, index) => [id, index]));
    // 최근 본 매장을 앞으로, 나머지는 카테고리 → 이름 순
    return [...options]
      .map((option) => ({
        ...option,
        recentIndex: recentRank.has(option.id) ? recentRank.get(option.id) : Infinity,
      }))
      .sort((a, b) => {
        if (a.recentIndex !== b.recentIndex) return a.recentIndex - b.recentIndex;
        if (a.category !== b.category) return String(a.category).localeCompare(String(b.category));
        return String(a.name).localeCompare(String(b.name), 'ko');
      });
  }, [options, recentStoreIds]);

  const selected = sorted.find((option) => option.id === value) ?? null;

  return (
    <Autocomplete
      size="small"
      options={sorted}
      value={selected}
      disabled={disabled}
      disableClearable
      openOnFocus
      groupBy={(option) =>
        option.recentIndex !== Infinity
          ? '최근 본 매장'
          : CATEGORY_LABELS[option.category] || option.category
      }
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, current) => option.id === current?.id}
      // 매장명과 code 둘 다 검색되게 한다
      filterOptions={(list, { inputValue }) =>
        list.filter((option) => matchesStoreKeyword(option, inputValue))
      }
      onChange={(event, next) => {
        if (next?.id) onChange(next.id);
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest} sx={{ display: 'block !important', py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {option.name}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.muted }}>
              {option.code}
            </Typography>
          </Box>
        );
      }}
      /*
        params 를 그대로 넘긴다 — inputProps 를 덮어쓰면 Autocomplete 가 input ref 를 잃는다.
        접근성 이름은 label 이 제공한다(role=combobox 의 accessible name).
      */
      renderInput={(params) => (
        <TextField
          {...params}
          label="점주 콘솔 매장 선택"
          placeholder={compact ? '매장 선택' : '매장명 또는 code'}
          sx={
            compact
              ? {
                  '& .MuiInputLabel-root': {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    m: -1,
                    p: 0,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    display: 'none',
                  },
                  '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                    height: layout.headerControlHeight,
                    minHeight: layout.headerControlHeight,
                    py: '0 !important',
                    pl: '13px !important',
                    pr: '38px !important',
                    borderRadius: `${radius.button}px`,
                    bgcolor: colors.surface,
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
                    },
                  },
                  '& .MuiAutocomplete-input.MuiInputBase-input': {
                    minWidth: '0 !important',
                    py: '0 !important',
                    px: '0 !important',
                    fontSize: 13.5,
                    fontWeight: 700,
                    lineHeight: `${layout.headerControlHeight}px`,
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  },
                }
              : undefined
          }
        />
      )}
      slotProps={{
        paper: { sx: { minWidth: 260 } },
        // 그룹 헤더는 MUI 기본 구조를 쓰고 스타일만 얹는다
        // (ul 안에 div 를 넣으면 마크업이 깨진다)
        listbox: {
          sx: {
            '& .MuiAutocomplete-groupLabel': {
              fontSize: 11,
              fontWeight: 800,
              color: colors.brand.primary,
              bgcolor: colors.brand.primarySoft,
              lineHeight: '28px',
            },
          },
        },
      }}
    />
  );
};

export default StorePicker;
